import { supabase } from '../lib/supabase';

function edgeInvokeErrorMessage(data: unknown, fnError: { message: string }): string {
  const fromBody =
    data && typeof data === 'object' && data !== null && 'error' in data
      ? String((data as { error: string }).error)
      : '';
  return fromBody || fnError.message || 'Request failed';
}

export interface LaunchCampaignBatchResult {
  success?: boolean;
  sent?: number;
  failed?: number;
  permanent?: number;
  remaining?: number;
  stopped?: 'rate_limited' | null;
  error?: string;
}

export async function launchRevenueCollectionCampaign(
  campaignId: string,
): Promise<LaunchCampaignBatchResult> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');
  const { data, error: fnError } = await supabase.functions.invoke('launch_campaign', {
    body: { campaign_id: campaignId },
  });
  if (fnError) {
    throw new Error(edgeInvokeErrorMessage(data, fnError));
  }
  return (data ?? {}) as LaunchCampaignBatchResult;
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Repeat launch batches until every eligible hotel has been attempted. */
export async function drainCampaignInitialSends(
  campaignId: string,
): Promise<{ permanent: number }> {
  const maxBatches = 40;
  let permanent = 0;
  for (let batch = 0; batch < maxBatches; batch++) {
    const body = await launchRevenueCollectionCampaign(campaignId);
    if (body.success !== true) {
      throw new Error(body.error || 'Launch failed');
    }
    permanent += body.permanent ?? 0;
    if (body.stopped === 'rate_limited') {
      await wait(40_000);
      continue;
    }
    const remaining = body.remaining ?? 0;
    if (remaining === 0) return { permanent };
    const progressed = (body.sent ?? 0) > 0 || (body.permanent ?? 0) > 0;
    if (!progressed) {
      throw new Error('Sending stopped before every hotel was invited. Try again in a few minutes.');
    }
  }
  throw new Error('Some hotels are still waiting for the initial invitation.');
}

export type AddCampaignHotelAction =
  | 'already_in_campaign'
  | 'inserted'
  | 'reactivated'
  | 'skipped_no_contact'
  | 'sent'
  | 'blocked_completed'
  | 'error';

export interface AddCampaignHotelResult {
  hotel_id: number;
  action: AddCampaignHotelAction;
  status: string;
  email_sent?: boolean;
  error?: string;
}

export interface AddHotelsToLaunchedCampaignResponse {
  success: boolean;
  results: AddCampaignHotelResult[];
}

export async function addHotelsToLaunchedCampaign(
  campaignId: string,
  hotelIds: number[],
): Promise<AddHotelsToLaunchedCampaignResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');
  const { data, error: fnError } = await supabase.functions.invoke('add_campaign_hotels', {
    body: { campaign_id: campaignId, hotel_ids: hotelIds },
  });
  if (fnError) {
    throw new Error(edgeInvokeErrorMessage(data, fnError));
  }
  if (!data || typeof data !== 'object' || !('results' in data)) {
    throw new Error('Invalid response from add_campaign_hotels');
  }
  return data as AddHotelsToLaunchedCampaignResponse;
}
